import { z } from 'zod';

const RgbaSchema = z.object({
    r: z.number().min(0).max(255),
    g: z.number().min(0).max(255),
    b: z.number().min(0).max(255),
    a: z.number().min(0).max(1),
});

type Rgba = z.infer<typeof RgbaSchema>;

// Accepts #RRGGBB or #AARRGGBB and transforms to RGBA object
const ColorSchema = z.string().regex(/^#[0-9A-F]{6}([0-9A-F]{2})?$/i, 'Invalid hex color format');

const StringLineStyle = z.enum(['solid', 'dashed', 'dotted', 'dashdot']).transform(style => {
    switch (style) {
        case 'solid':
            return 'solid';
        case 'dashed':
            return 'dash';
        case 'dotted':
            return 'dot';
        case 'dashdot':
            return 'dashdot';
        default:
            return 'solid';
    }
});

const RgbaColorSchema = ColorSchema.transform((hex): Rgba => {
    // Remove the # prefix
    const value = hex.slice(1);

    if (value.length === 6) {
        // #RRGGBB format (no alpha)
        return {
            r: parseInt(value.slice(0, 2), 16),
            g: parseInt(value.slice(2, 4), 16),
            b: parseInt(value.slice(4, 6), 16),
            a: 1,
        };
    } else {
        // #AARRGGBB format (with alpha)
        return {
            a: parseInt(value.slice(0, 2), 16) / 255,
            r: parseInt(value.slice(2, 4), 16),
            g: parseInt(value.slice(4, 6), 16),
            b: parseInt(value.slice(6, 8), 16),
        };
    }
});

const AttributeSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['text', 'real', 'integer', 'number']), // Add 'number'
});

const CodeGroupSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    deactivated_for_measurements: z.boolean().nullable().optional(),
});

const CodeSchema = z.object({
    name: z.string().optional(), // Make optional for partial entries
    shortdesc: z.string().optional(),
    fulldesc: z.string().optional(),
    type: z.enum(['point', 'line', 'category', 'area', 'surface']), // Make optional
    pointcolor: ColorSchema.optional(),
    pointstyle: z.string().optional(),
    linecolor: ColorSchema.optional(),
    linestyle: z.enum(['solid', 'dashed', 'dotted', 'dashdot']).optional(),
    linewidth: z.number().optional(),
    surfacecolor: ColorSchema.optional(),
    codegroups: z.array(z.string()).optional().default([]),
    attribute_refs: z.array(z.unknown()).optional(),
});

const MetaSchema = z.object({
    at: z.iso.datetime(),
    author: z.string().optional().nullable(),
    contrib: z.string().optional().nullable(),
    id: z.uuid(),
    type: z.literal('codelist'),
    v: z.string(), // Change to plain string since format is non-standard
});

const CodeListSchema = z.object({
    name: z.string(),
    timestamp: z.iso.datetime().nullable().optional(),
    creator: z.string(),
    application: z.string().optional(),
    applicationversion: z.string().optional(),
    libraryversion: z.string().optional(),
    codes: z.array(CodeSchema),
    attributes: z.array(AttributeSchema),
    codegroups: z.array(CodeGroupSchema),
    meta: MetaSchema,
});

const CodelistDataSchema = z.object({
    codelist: CodeListSchema,
    id: z.number(),
    uuid: z.uuid(),
});

const IsoCodelistsResponseSchema = z.object({
    links: z.any(),
    data: z.array(CodeListSchema),
    limit: z.number(),
});

const CodelistsResponseSchema = z.array(CodelistDataSchema);

const SurfacePropertiesSchema = z
    .object({
        infraCode: z.string().optional(),
        name: z.string().optional(),
    })
    .refine(data => data.infraCode || data.name, {
        message: 'infraCode or name is required in SurfaceProperties',
    });

const PointPropertiesSchema = z.object({
    code: z.string(),
});

export type StringLineStyle = z.infer<typeof StringLineStyle>;
export type RGBAColor = z.infer<typeof RgbaColorSchema>;
export type CodelistData = z.infer<typeof CodelistDataSchema>;
export type CodelistsResponse = z.infer<typeof CodelistsResponseSchema>;
export type CodeList = z.infer<typeof CodeListSchema>;
export type Code = z.infer<typeof CodeSchema>;
export type Attribute = z.infer<typeof AttributeSchema>;
export type CodeGroup = z.infer<typeof CodeGroupSchema>;
export type Meta = z.infer<typeof MetaSchema>;
export type ISOCodelistResponse = z.infer<typeof IsoCodelistsResponseSchema>;

export type SurfaceProperties = z.infer<typeof SurfacePropertiesSchema>;
export type PointProperties = z.infer<typeof PointPropertiesSchema>;

export type CodeIndex = Map<string, Code>;

export {
    CodeListSchema,
    CodeSchema,
    AttributeSchema,
    CodeGroupSchema,
    MetaSchema,
    ColorSchema,
    CodelistsResponseSchema,
    IsoCodelistsResponseSchema,
    RgbaColorSchema,
    SurfacePropertiesSchema,
    PointPropertiesSchema,
    StringLineStyle,
};
